import { useEffect, useRef, useState } from "react";

type Props = {
  onComplete: (search: string) => void;
  placeHolder: string;
  className?: string;
  value?: string;
  minLength?: number;
  onClear?: () => void;
};

export default function AutocompleteInput({
  onComplete,
  placeHolder,
  className = "",
  value = "",
  minLength = 2,
  onClear,
}: Props) {
  const [inputValue, setInputValue] = useState(value);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const didMountRef = useRef(false); // 🔹 Add mount guard

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return; // 🔹 Skip effect on first render
    }

    if (inputValue.trim().length < minLength) {
      if (onClear) onClear();
      onComplete("");
      return;
    }

    setSearchLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      onComplete(inputValue.trim());
      setSearchLoading(false);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  return (
    <div className="w-full">
      <div
        className={`relative flex items-center border border-base-300 bg-base-200 rounded-2xl px-4 h-8 shadow-xl focus-within:ring-2 focus-within:ring-primary transition duration-200 ${className}`}
      >
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeHolder}
          className="w-full bg-transparent focus:outline-none text-sm text-base-content placeholder:text-base-content/50"
        />
        {searchLoading && (
          <span className="absolute right-4">
            <span className="loading loading-spinner loading-sm text-base-content/50" />
          </span>
        )}
      </div>
    </div>
  );
}
